/**
 * ItemCard Component (Paiseh A.dc design refresh)
 * Displays a wishlist item as either a horizontal row (default) or a
 * product-shot grid card ("this is what friends see" layout).
 */

import { useState } from 'react'
import { WishlistItem } from '@/types'
import { useAuth } from '@/contexts/AuthContext'
import { SWATCH_AVAIL, SWATCH_TAKEN } from '@/utils/occasionTheme'

interface ItemCardProps {
  item: WishlistItem
  isOwner: boolean
  onEdit?: (item: WishlistItem) => void
  onDelete?: (item: WishlistItem) => void
  onReserve?: (item: WishlistItem) => Promise<void>
  showReserveButton?: boolean
  /** 'row' = compact horizontal card, 'grid' = product-shot card for a grid */
  variant?: 'row' | 'grid'
}

const PRIORITY_LABELS: Record<WishlistItem['priority'], string> = {
  low: 'Nice to have',
  medium: 'Would love',
  high: 'Dying for it',
}

const priorityColors: Record<WishlistItem['priority'], string> = {
  low: 'bg-[#F5EFE5] text-[#8A7E72]',
  medium: 'bg-paiseh-peach text-paiseh-orangeDark',
  high: 'bg-paiseh-orange/15 text-paiseh-orange',
}

const formatPrice = (price: number | null, currency: string) => {
  if (!price) return null
  const formatter = new Intl.NumberFormat('en-SG', { style: 'currency', currency: currency || 'SGD' })
  return formatter.format(price / 100)
}

const getStore = (url: string | null) => {
  if (!url) return null
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return null
  }
}

export const ItemCard = ({
  item,
  isOwner,
  onEdit,
  onDelete,
  onReserve,
  showReserveButton = false,
  variant = 'row',
}: ItemCardProps) => {
  const { user } = useAuth()
  const [isReserving, setIsReserving] = useState(false)

  const isReservedByMe = item.isReserved && item.reservedBy === user?.uid
  const canShowReserveButton = showReserveButton && !!user

  const handleReserve = async () => {
    if (!onReserve) return
    setIsReserving(true)
    try {
      await onReserve(item)
    } finally {
      setIsReserving(false)
    }
  }

  // ============================= GRID CARD =============================
  if (variant === 'grid') {
    const dimmed = !isOwner && item.isReserved && !isReservedByMe
    const store = getStore(item.url)
    const meta = [store, PRIORITY_LABELS[item.priority]].filter(Boolean).join(' · ')

    return (
      <div
        className="bg-[#FDFBF7] rounded-[22px] overflow-hidden border border-ink/[.06] flex flex-col transition-shadow hover:shadow-[0_10px_28px_rgba(43,36,32,.07)]"
        style={{ opacity: dimmed ? 0.6 : 1 }}
      >
        {/* product shot */}
        <div
          className="h-[150px] relative bg-cover bg-center"
          style={{
            background: item.imageUrl
              ? `center/cover no-repeat url(${item.imageUrl})`
              : item.isReserved
                ? SWATCH_TAKEN
                : SWATCH_AVAIL,
          }}
        >
          {item.isReserved && (
            <span
              className={`absolute top-3 right-3 text-xs font-bold px-3 py-1.5 rounded-full ${
                isReservedByMe || isOwner
                  ? 'bg-paiseh-olive text-white'
                  : 'bg-[#EFE7DA] text-[#9A8E80]'
              }`}
            >
              ✓ {isReservedByMe ? 'Reserved by you' : 'Reserved'}
            </span>
          )}
        </div>

        {/* body */}
        <div className="p-[18px] flex flex-col flex-1">
          <div className="flex justify-between items-start gap-2.5">
            <span className="font-bold text-base leading-[1.25]">{item.title}</span>
            {item.price && (
              <span className="font-bold text-paiseh-orange whitespace-nowrap">
                {formatPrice(item.price, item.currency)}
              </span>
            )}
          </div>

          <div className="text-[13px] text-paiseh-faint mt-[5px] flex items-center gap-2 flex-wrap">
            {meta && <span>{meta}</span>}
            {item.url && (
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-paiseh-orange font-semibold hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                View ↗
              </a>
            )}
          </div>

          {/* actions */}
          <div className="mt-auto pt-3.5">
            {isOwner ? (
              <div className="flex gap-2">
                <button
                  onClick={() => onEdit?.(item)}
                  className="flex-1 text-center font-bold text-sm py-3 rounded-full bg-[#FCF2E7] text-paiseh-orangeDark hover:bg-paiseh-peach transition-colors"
                >
                  Edit
                </button>
                <button
                  onClick={() => onDelete?.(item)}
                  className="px-4 rounded-full border border-ink/[.12] text-paiseh-faint hover:text-red-600 hover:border-red-300 transition-colors"
                  title="Delete item"
                  aria-label="Delete item"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            ) : item.isReserved ? (
              <div
                className={`text-center font-bold text-sm py-3 rounded-full ${
                  isReservedByMe
                    ? 'bg-paiseh-oliveTint text-[#4E7241]'
                    : 'bg-[#EFE7DA] text-[#9A8E80]'
                }`}
              >
                {isReservedByMe ? 'Reserved by you' : 'Already reserved'}
              </div>
            ) : canShowReserveButton ? (
              <button
                onClick={handleReserve}
                disabled={isReserving}
                className="w-full text-center font-bold text-sm py-3 rounded-full bg-paiseh-orange text-white hover:opacity-95 disabled:opacity-50 transition-opacity"
              >
                {isReserving ? 'Reserving…' : 'Reserve this gift'}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    )
  }

  // ============================= ROW CARD =============================
  return (
    <div
      className="bg-white rounded-[22px] border border-ink/[.06] p-4 transition-shadow hover:shadow-[0_10px_28px_rgba(43,36,32,.07)]"
      style={{ opacity: !isOwner && item.isReserved && !isReservedByMe ? 0.72 : 1 }}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-3">
            <div
              className="w-16 h-16 rounded-[14px] flex-shrink-0 bg-cover bg-center"
              style={{
                background: item.imageUrl
                  ? `center/cover no-repeat url(${item.imageUrl})`
                  : SWATCH_AVAIL,
              }}
            />
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-ink mb-1 leading-[1.25]">{item.title}</h3>
              {item.description && (
                <p className="text-sm text-paiseh-muted mb-2 line-clamp-2">{item.description}</p>
              )}

              <div className="flex items-center gap-3 flex-wrap">
                {item.price && (
                  <span className="text-sm font-bold text-paiseh-orange">
                    {formatPrice(item.price, item.currency)}
                  </span>
                )}

                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${priorityColors[item.priority]}`}>
                  {PRIORITY_LABELS[item.priority]}
                </span>

                {item.url && (
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-semibold text-paiseh-orange hover:underline flex items-center gap-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    View ↗
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {isOwner ? (
            <>
              {item.isReserved && (
                <span className="text-xs px-3 py-1.5 rounded-full bg-paiseh-oliveTint text-[#4E7241] font-bold flex items-center gap-1">
                  ✓ Reserved
                </span>
              )}
              <button
                onClick={() => onEdit?.(item)}
                className="p-2 text-paiseh-faint hover:text-paiseh-orange transition-colors"
                title="Edit item"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
              <button
                onClick={() => onDelete?.(item)}
                className="p-2 text-paiseh-faint hover:text-red-600 transition-colors"
                title="Delete item"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </>
          ) : (
            <>
              {isReservedByMe ? (
                <span className="text-xs px-3 py-1.5 rounded-full bg-paiseh-oliveTint text-[#4E7241] font-bold flex items-center gap-1">
                  ✓ Reserved by you
                </span>
              ) : item.isReserved ? (
                <span className="text-xs px-3 py-1.5 rounded-full bg-[#EFE7DA] text-[#9A8E80] font-bold flex items-center gap-1">
                  ✓ Reserved
                </span>
              ) : canShowReserveButton ? (
                <button
                  onClick={handleReserve}
                  disabled={isReserving}
                  className="px-5 py-2.5 rounded-full font-bold text-sm bg-paiseh-orange text-white hover:opacity-95 disabled:opacity-50 transition-opacity"
                >
                  {isReserving ? 'Reserving…' : 'Reserve this gift'}
                </button>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
